import { LogObject, aiChatUouActionsParams, dummyNameParams, formErrorAppearedDissapearedParams, formFieldEditParams, formFieldFocusParams, formViewParams, submitFormClickParams } from '../types';

/**
 * A function to create a log object for the 'dummyName' event (evid: 1000)
 *
 * Event description: dummy-name.
 *
 * @param {dummyNameParams} params - The parameters for this event
 * @property {GUID} form_guid - form_guid
 * @property {string} namespace - namespace
 * @return {LogObject<dummyNameParams>} LogObject<dummyNameParams>
 */
export function dummyName(params: dummyNameParams): LogObject<dummyNameParams> {
  return { evid: 1000, src: 156, endpoint: 'forms-platform-uou', params };
}

/**
 * A function to create a log object for the 'formView' event (evid: 1001)
 *
 * Event description: Tracks when a visitor views a form on a site. The event captures details such as the form type, the viewing mode, and whether it was presented in a classic or chat format.
 *
 * @param {formViewParams} params - The parameters for this event
 * @property {string} form_filling_id - form_filling_id
 * @property {string} form_schema_name - form schema name
 * @property {string} form_url - form url
 * @property {string} form_type - form expression (web, standalone, etc.)
 * @property {string} mode - preview/live site
 * @property {GUID} form_guid - form_guid
 * @property {string} namespace - namespace
 * @property {boolean} is_standalone - is standalone form
 * @property {string} filling_method - chat or classic, for standalone
 * @return {LogObject<formViewParams>} LogObject<formViewParams>
 */
export function formView(params: formViewParams): LogObject<formViewParams> {
  return { evid: 1001, src: 156, endpoint: 'forms-platform-uou', params };
}

/**
 * A function to create a log object for the 'formFieldEdit' event (evid: 1002)
 *
 * Event description: Fires when a visitor starts editing a field in a form within the Forms Platform. This event captures details about the specific form and field being edited, including the form type and the filling method used, such as classic or chat.
 *
 * @param {formFieldEditParams} params - The parameters for this event
 * @property {string} field_label - id
 * @property {string} form_schema_name - form schama name
 * @property {string} field_id - field_id
 * @property {string} form_url - form url
 * @property {string} field_target - id: field_target
 * @property {string} form_type - form expression (web, standalone, etc.)
 * @property {GUID} form_guid - form_guid
 * @property {string} form_fill_session_id - unique id which defines particular form fill session
 * @property {string} namespace - namespace
 * @property {boolean} is_standalone - is standalone form
 * @property {string} filling_method - chat or classic, for standalone
 * @return {LogObject<formFieldEditParams>} LogObject<formFieldEditParams>
 */
export function formFieldEdit(params: formFieldEditParams): LogObject<formFieldEditParams> {
  return { evid: 1002, src: 156, endpoint: 'forms-platform-uou', params };
}

/**
 * A function to create a log object for the 'submitFormClick' event (evid: 1003)
 *
 * Event description: Fires when a visitor submits a form using the Forms Platform. The event includes the submission status (success or failed), the filling method used, and the source of the submission.
 *
 * @param {submitFormClickParams} params - The parameters for this event
 * @property {string} form_schema_name - form schema name
 * @property {string} submission_status - Success/Failed
 * @property {string} form_url - form url
 * @property {string} error_reason - Error reason if failed
 * @property {GUID} form_guid - form_guid
 * @property {string} error_message - Full error message
 * @property {string} form_fill_session_id - unique id which defines particular form fill session
 * @property {string} namespace - namespace
 * @property {GUID} submission_id - submission id
 * @property {boolean} is_standalone - is standalone form
 * @property {string} filling_method - chat or classic, for standalone
 * @property {string} referral_info - Where submission was made from (website form, ai assistant, etc)
 * @return {LogObject<submitFormClickParams>} LogObject<submitFormClickParams>
 */
export function submitFormClick(params: submitFormClickParams): LogObject<submitFormClickParams> {
  return { evid: 1003, src: 156, endpoint: 'forms-platform-uou', params };
}

/**
 * A function to create a log object for the 'formFieldFocus' event (evid: 1004)
 *
 * Event description: Fires when a visitor interacts with a form by focusing on one of its fields. This event captures details about the form interaction, including the session ID, whether the form was standalone, and the method used for filling it.
 *
 * @param {formFieldFocusParams} params - The parameters for this event
 * @property {string} field_label - id
 * @property {string} form_schema_name - form schama name
 * @property {string} field_id - field id
 * @property {string} form_url - form url
 * @property {string} field_target - id
 * @property {GUID} form_guid - form schema id
 * @property {string} action_type - blur/focus
 * @property {string} form_fill_session_id - unique id which defines particular form fill session
 * @property {string} namespace - namespace
 * @property {boolean} is_standalone - is standalone form
 * @property {string} filling_method - chat or classic, for standalone
 * @return {LogObject<formFieldFocusParams>} LogObject<formFieldFocusParams>
 */
export function formFieldFocus(params: formFieldFocusParams): LogObject<formFieldFocusParams> {
  return { evid: 1004, src: 156, endpoint: 'forms-platform-uou', params };
}

/**
 * A function to create a log object for the 'formErrorAppearedDissapeared' event (evid: 1006)
 *
 * Event description: Fires when an error message appears or disappears for a visitor interacting with a form field. The event specifies whether the error appeared or disappeared, and includes details such as the error name and the form filling method.
 *
 * @param {formErrorAppearedDissapearedParams} params - The parameters for this event
 * @property {string} field_label - id
 * @property {string} form_schema_name - Form schema name
 * @property {string} field_id - field id
 * @property {string} form_url - form url
 * @property {string} field_target - id
 * @property {GUID} form_guid - form schema id
 * @property {string} field_name - name of the field in schema
 * @property {string} action_type - appeared/dissapeared
 * @property {string} form_fill_session_id - unique id which defines particular form fill session
 * @property {string} error_name - description of the error appeared
 * @property {string} namespace - namespace
 * @property {boolean} is_standalone - is standalone form
 * @property {string} filling_method - chat or classic, for standalone
 * @return {LogObject<formErrorAppearedDissapearedParams>} LogObject<formErrorAppearedDissapearedParams>
 */
export function formErrorAppearedDissapeared(params: formErrorAppearedDissapearedParams): LogObject<formErrorAppearedDissapearedParams> {
  return { evid: 1006, src: 156, endpoint: 'forms-platform-uou', params };
}

/**
 * A function to create a log object for the 'aiChatUouActions' event (evid: 3221)
 *
 * Event description: Actions of UoU while filling the form through chat - open, close, send message
 *
 * @param {aiChatUouActionsParams} params - The parameters for this event
 * @property {string} fieldsFilled - Fields filled by AI assistant in a form
 * @property {string} errorMessage - Error message if error
 * @property {string} field_id - field_id
 * @property {GUID} form_guid - form_guid
 * @property {string} fieldsTotal - Total fields in a form
 * @property {string} form_fill_session_id - session for form filling (if possible)
 * @property {string} action - open/close/send/error message
 * @property {string} namespace - namespace
 * @return {LogObject<aiChatUouActionsParams>} LogObject<aiChatUouActionsParams>
 */
export function aiChatUouActions(params: aiChatUouActionsParams): LogObject<aiChatUouActionsParams> {
  return { evid: 3221, src: 156, endpoint: 'forms-platform-uou', params };
}
